import { useState, useEffect, useCallback } from 'react'
import type { User, LoginCredentials } from '../types'
import { post, get } from '../helper/network'
import API from '../helper/api'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('user'))
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminChecked, setAdminChecked] = useState(false)

  const checkAdminStatus = useCallback(async () => {
    try {
      const res: any = await get(API.GET_ADMIN_STATUS)
      if (res && res.code === 200 && res.data) {
        setIsAdmin(res.data.admin === true)
      } else {
        setIsAdmin(false)
      }
    } catch {
      setIsAdmin(false)
    } finally {
      setAdminChecked(true)
    }
  }, [])

  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser)
        setUser(parsed)
        checkAdminStatus()
      } catch {}
    } else {
      setAdminChecked(true)
    }
  }, [checkAdminStatus])

  // 登录后拉取完整用户信息（含企业信息）
  const fetchUserInfo = async (username: string) => {
    try {
      const res: any = await get(API.GET_USER_INFO, { username })
      if (res && res.code === 200 && res.data) {
        const d = res.data
        const enriched: User = {
          username: d.userName || username,
          nickName: d.nickName || username,
          avatarUrl: d.avatarUrl,
          enterpriseId: d.enterpriseId,
          enterpriseName: d.enterpriseName,
          appId: d.appId,
          appName: d.appName,
          tokenQuota: d.tokenQuota,
          tokenUsed: d.tokenUsed,
          role: d.role,
        }
        setUser(enriched)
        localStorage.setItem('user', JSON.stringify(enriched))
        checkAdminStatus()
      }
    } catch {}
  }

  // 主动刷新用户信息（头像更新等场景）
  const refreshUserInfo = () => {
    if (user?.username) {
      fetchUserInfo(user.username)
    }
  }

  const loginDirect = (username: string, jsessionId: string) => {
    const userData: User = { username, nickName: username }
    setUser(userData)
    setIsLoggedIn(true)
    localStorage.setItem('user', JSON.stringify(userData))
    localStorage.setItem('cookie', jsessionId)
    fetchUserInfo(username)
  }

  const loginByPhone = (phone: string, jsessionId: string) => {
    const username = `user${phone}`
    const userData: User = { username, nickName: phone }
    setUser(userData)
    setIsLoggedIn(true)
    localStorage.setItem('user', JSON.stringify(userData))
    localStorage.setItem('cookie', jsessionId)
    fetchUserInfo(username)
  }

  const login = (credentials: LoginCredentials) => {
    const { username, password, nickName, mode, phone } = credentials
    let api = ''
    let params: Record<string, string> = {}

    if (mode === 'login') {
      api = API.POST_LOGIN
      params = { username, password }
    } else {
      api = API.POST_REGISTER
      params = { nickName, password, phone: phone || username }
    }

    post(api, params).then((res: any) => {
      if (res && res.code === 200) {
        if (api === API.POST_LOGIN) {
          const u = { username, nickName: nickName || username }
          setUser(u)
          setIsLoggedIn(true)
          localStorage.setItem('user', JSON.stringify(u))
          localStorage.setItem('cookie', res.data)
          fetchUserInfo(username)
        }
      }
    })
  }

  const logout = () => {
    get(API.GET_LOGOUT).catch(() => {})
    setUser(null)
    setIsLoggedIn(false)
    setIsAdmin(false)
    setAdminChecked(false)
    localStorage.removeItem('user')
    localStorage.removeItem('cookie')
  }

  return {
    user,
    isLoggedIn,
    isAdmin,
    adminChecked,
    login,
    loginDirect,
    loginByPhone,
    refreshUserInfo,
    logout,
  }
}
